import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import FileAttachments from "@/components/FileAttachments";
import { confirmDialog } from "@/components/ConfirmDialog";

const CATEGORIES = ["DFI", "Investor", "Sponsor", "Family Office", "Government", "Regulator", "Operator", "Other"];

const emptyContact = {
  name: "",
  organization: "",
  role: "",
  email: "",
  phone: "",
  country: "",
  category: "Other",
  tags: [],
  notes: "",
};

function PageHeader({ overline, title, action }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream flex items-end justify-between">
      <div>
        <div className="overline text-gold-deep">{overline}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
        <div className="gold-rule mt-4" />
      </div>
      {action}
    </div>
  );
}

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyContact);

  const load = async () => {
    const { data } = await api.get("/contacts");
    setContacts(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyContact);
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...emptyContact, ...c, tags: c.tags || [] });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/contacts/${editing}`, form);
      else await api.post("/contacts", form);
      toast.success(editing ? "Contact updated" : "Contact created");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete this contact?", description: "The contact will be permanently removed. Linked deals and outreach records keep their references but lose the join.", confirmLabel: "Delete", destructive: true }))) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Contact deleted");
    load();
  };

  const filtered = contacts.filter((c) => {
    const q = search.toLowerCase();
    return !q || [c.name, c.organization, c.role, c.country, c.category, (c.tags || []).join(" ")]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });

  return (
    <div data-testid="contacts-page">
      <PageHeader
        overline="Network"
        title="Contacts"
        action={
          <button
            onClick={openCreate}
            data-testid="contacts-add-btn"
            className="bg-navy text-gold px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2"
          >
            <Plus className="h-3.5 w-3.5" /> Add contact
          </button>
        }
      />
      <div className="p-8">
        <input
          data-testid="contacts-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, org, role, country, tag…"
          className="w-full max-w-md mb-6 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold text-sm"
        />

        <div className="bg-white border border-navy/10 overflow-hidden">
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead className="bg-navy/5 text-navy/70">
              <tr>
                <th className="text-left px-4 py-3 overline">Name</th>
                <th className="text-left px-4 py-3 overline">Organization</th>
                <th className="text-left px-4 py-3 overline">Role</th>
                <th className="text-left px-4 py-3 overline">Country</th>
                <th className="text-left px-4 py-3 overline">Category</th>
                <th className="text-right px-4 py-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-navy/5 hover:bg-cream/40" data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{c.name}</td>
                  <td className="px-4 py-3 text-navy/80">{c.organization}</td>
                  <td className="px-4 py-3 text-navy/70">{c.role}</td>
                  <td className="px-4 py-3 text-navy/70">{c.country}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 border border-gold/40 text-gold-deep text-[10px] uppercase tracking-wider">
                      {c.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(c)} data-testid={`contact-edit-${c.id}`} className="p-1.5 text-navy/60 hover:text-gold">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(c.id)} data-testid={`contact-delete-${c.id}`} className="p-1.5 text-navy/60 hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-navy/40 italic">No contacts yet. Add your first one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="contact-form-modal">
          <form onSubmit={save} className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline text-gold-deep">{editing ? "Edit" : "New"}</div>
                <h2 className="font-serif text-2xl text-navy">{editing ? "Edit contact" : "Add contact"}</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} data-testid="contact-form-close" className="text-navy/50 hover:text-navy">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-5">
              {[
                ["name", "Name *", true],
                ["organization", "Organization"],
                ["role", "Role"],
                ["country", "Country"],
                ["email", "Email"],
                ["phone", "Phone"],
              ].map(([key, label, required]) => (
                <div key={key}>
                  <label className="overline text-navy/60">{label}</label>
                  <input
                    data-testid={`contact-input-${key}`}
                    required={required}
                    value={form[key] || ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                  />
                </div>
              ))}
              <div className="col-span-2">
                <label className="overline text-navy/60">Category</label>
                <select
                  data-testid="contact-input-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="overline text-navy/60">Tags (comma-separated)</label>
                <input
                  data-testid="contact-input-tags"
                  value={(form.tags || []).join(", ")}
                  onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold"
                />
              </div>
              <div className="col-span-2">
                <label className="overline text-navy/60">Notes</label>
                <textarea
                  data-testid="contact-input-notes"
                  rows={4}
                  value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm"
                />
              </div>
              {editing && (
                <div className="col-span-2">
                  <FileAttachments contactId={editing} />
                </div>
              )}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-navy/70 hover:text-navy">
                Cancel
              </button>
              <button type="submit" data-testid="contact-form-save" className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600">
                {editing ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
